'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

interface InfoTooltipProps {
  info: string;
  ariaLabel?: string;
  className?: string;
}

export default function InfoTooltip({ info, ariaLabel, className }: InfoTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [bubbleStyle, setBubbleStyle] = useState<CSSProperties>({
    visibility: 'hidden',
  });
  const hostRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  // Position with `position: fixed` so the bubble never affects document
  // scroll-width — DaisyUI's `::before` uses `position: absolute` with
  // `transform: translateX(-50%)` which can't be overridden by Tailwind's
  // `translate` utilities and overflows the viewport at right-edge triggers.
  const place = useCallback(() => {
    const host = hostRef.current;
    const bubble = bubbleRef.current;
    if (!host || !bubble) return;
    const hostRect = host.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const margin = 8;

    const triggerCenterX = hostRect.left + hostRect.width / 2;
    let left = triggerCenterX - bubbleRect.width / 2;
    left = Math.max(
      margin,
      Math.min(left, window.innerWidth - bubbleRect.width - margin),
    );

    const top = hostRect.top - bubbleRect.height - 8;

    setBubbleStyle({
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      visibility: 'visible',
    });
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      setBubbleStyle({ visibility: 'hidden' });
      return;
    }
    place();
    const onResize = () => place();
    const onScroll = () => place();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [visible, place]);

  return (
    <span
      ref={hostRef}
      className={`relative inline-flex items-center shrink-0 ${className ?? ''}`}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <button
        type="button"
        className="text-base-content/30 hover:text-base-content cursor-pointer inline-flex items-center"
        onClick={(e) => {
          e.stopPropagation();
          setVisible((v) => !v);
        }}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        aria-label={ariaLabel ?? 'More information'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 6a1 1 0 112 0 1 1 0 01-2 0zm0 3a1 1 0 011-1h.01a1 1 0 01.99 1.117l-.706 5.65A.5.5 0 0012.79 17H13a1 1 0 110 2h-.21a2.5 2.5 0 01-2.48-2.81l.7-5.61A1 1 0 0111 11z"
          />
        </svg>
      </button>
      {visible && (
        <span
          ref={bubbleRef}
          role="tooltip"
          style={bubbleStyle}
          className="z-50 max-w-xs whitespace-normal rounded-md bg-primary px-3 py-2 text-xs text-primary-content shadow-lg pointer-events-none"
        >
          {info}
        </span>
      )}
    </span>
  );
}
